import React, { useState, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logo from "../AdmissionForm/logo.png";
import "./FeeReceipt.css";

const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = [
  "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
];

function twoDigits(n) {
  if (n < 20) return ONES[n];
  return TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
}

function threeDigits(n) {
  if (n < 100) return twoDigits(n);
  return ONES[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + twoDigits(n % 100) : "");
}

// Converts a number to words using the Indo-Pak numbering system (Lakh / Crore)
function numberToWords(num) {
  const n = Math.round(Number(num) || 0);
  if (n === 0) return "Zero";
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rest = n % 1000;

  let parts = [];
  if (crore) parts.push(threeDigits(crore) + " Crore");
  if (lakh) parts.push(threeDigits(lakh) + " Lakh");
  if (thousand) parts.push(threeDigits(thousand) + " Thousand");
  if (rest) parts.push(threeDigits(rest));

  return parts.join(" ").trim();
}

function formatMoney(num) {
  const n = Number(num) || 0;
  return n.toLocaleString("en-PK");
}

const emptyForm = {
  studentName: "",
  fatherName: "",
  formNo: "",
  course: "",
  paymentMode: "Cash",
  feeType: "Full Payment",
  totalFee: "",
  previouslyPaid: "",
  amountPaid: "",
  installmentNo: "",
  totalInstallments: "",
  dueDate: "",
  remarks: "",
};

function todayFormatted() {
  const d = new Date();
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function FeeReceipt() {
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(null);
  const [errors, setErrors] = useState({});
  const previewRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["totalFee", "previouslyPaid", "amountPaid"].includes(name)) {
      setForm((f) => ({ ...f, [name]: value.replace(/[^\d.]/g, "") }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const isInstallment = form.feeType === "Installment";

  const validate = () => {
    const err = {};
    if (!form.studentName.trim()) err.studentName = "Student name is required";
    if (!form.formNo.trim()) err.formNo = "Form / Roll No. is required";
    if (!form.course.trim()) err.course = "Course / language is required";
    if (!form.totalFee.trim()) err.totalFee = "Total fee is required";
    if (!form.amountPaid.trim()) err.amountPaid = "Amount paid is required";
    else if (Number(form.amountPaid) <= 0) err.amountPaid = "Enter a valid amount";
    if (isInstallment) {
      if (!form.installmentNo.trim()) err.installmentNo = "Required";
      if (!form.totalInstallments.trim()) err.totalInstallments = "Required";
    }
    return err;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length === 0) {
      const totalFee = Number(form.totalFee) || 0;
      const previouslyPaid = Number(form.previouslyPaid) || 0;
      const amountPaid = Number(form.amountPaid) || 0;
      const balance = Math.max(totalFee - previouslyPaid - amountPaid, 0);
      setSubmitted({
        ...form,
        totalFee,
        previouslyPaid,
        amountPaid,
        balance,
        date: todayFormatted(),
      });
    }
  };

  const handleEdit = () => setSubmitted(null);

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setSubmitted(null);
  };

  const handleDownloadPDF = async () => {
    const node = previewRef.current;
    if (!node) return;

    const canvas = await html2canvas(node, {
      scale: 3,
      backgroundColor: "#faf6ec",
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth - 24;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const x = (pageWidth - imgWidth) / 2;
    const y = Math.min(15, (pageHeight - imgHeight) / 2);

    pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
    pdf.save(`${submitted.studentName.replace(/\s+/g, "_")}_fee_receipt.pdf`);
  };

  return (
    <div className="fee-page">
      <div className="fee-wrap">
        <header className="fee-header">
          <img
            src={logo}
            alt="Institute of Applied Languages"
            className="fee-logo"
            style={{ width: 78, height: 78, objectFit: "cover", borderRadius: "50%" }}
          />
          <span className="fee-eyebrow">Institute Of Applied Languages</span>
          <h1 className="fee-title">Fee Submission Receipt</h1>
          <p className="fee-subtitle">Fill in the payment details below to generate a receipt.</p>
        </header>

        {!submitted ? (
          <form className="fee-card" onSubmit={handleSubmit} noValidate>
            <div className="fee-grid">
              <div className="fee-field">
                <label htmlFor="studentName">Student Name</label>
                <input
                  id="studentName"
                  name="studentName"
                  type="text"
                  placeholder="e.g. Ayesha Khan"
                  value={form.studentName}
                  onChange={handleChange}
                />
                {errors.studentName && <span className="fee-error">{errors.studentName}</span>}
              </div>

              <div className="fee-field">
                <label htmlFor="fatherName">Father Name</label>
                <input
                  id="fatherName"
                  name="fatherName"
                  type="text"
                  placeholder="e.g. Imran Khan"
                  value={form.fatherName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="fee-grid">
              <div className="fee-field">
                <label htmlFor="formNo">Form / Roll No.</label>
                <input
                  id="formNo"
                  name="formNo"
                  type="text"
                  placeholder="e.g. IAL-2026-8572"
                  value={form.formNo}
                  onChange={handleChange}
                />
                {errors.formNo && <span className="fee-error">{errors.formNo}</span>}
              </div>

              <div className="fee-field">
                <label htmlFor="course">Course / Language</label>
                <input
                  id="course"
                  name="course"
                  type="text"
                  placeholder="e.g. French - Beginner"
                  value={form.course}
                  onChange={handleChange}
                />
                {errors.course && <span className="fee-error">{errors.course}</span>}
              </div>
            </div>

            <div className="fee-field">
              <label>Payment Type</label>
              <div className="fee-toggle-group">
                {["Full Payment", "Installment"].map((t) => (
                  <label
                    key={t}
                    className={`fee-toggle-option ${form.feeType === t ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="feeType"
                      value={t}
                      checked={form.feeType === t}
                      onChange={handleChange}
                    />
                    {t}
                  </label>
                ))}
              </div>
            </div>

            {isInstallment && (
              <div className="fee-grid">
                <div className="fee-field">
                  <label htmlFor="installmentNo">Installment No.</label>
                  <input
                    id="installmentNo"
                    name="installmentNo"
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 2"
                    value={form.installmentNo}
                    onChange={handleChange}
                  />
                  {errors.installmentNo && (
                    <span className="fee-error">{errors.installmentNo}</span>
                  )}
                </div>

                <div className="fee-field">
                  <label htmlFor="totalInstallments">Total Installments</label>
                  <input
                    id="totalInstallments"
                    name="totalInstallments"
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 4"
                    value={form.totalInstallments}
                    onChange={handleChange}
                  />
                  {errors.totalInstallments && (
                    <span className="fee-error">{errors.totalInstallments}</span>
                  )}
                </div>
              </div>
            )}

            <div className="fee-grid">
              <div className="fee-field">
                <label htmlFor="totalFee">Total Course Fee (PKR)</label>
                <input
                  id="totalFee"
                  name="totalFee"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 30000"
                  value={form.totalFee}
                  onChange={handleChange}
                />
                {errors.totalFee && <span className="fee-error">{errors.totalFee}</span>}
              </div>

              <div className="fee-field">
                <label htmlFor="previouslyPaid">Previously Paid (PKR)</label>
                <input
                  id="previouslyPaid"
                  name="previouslyPaid"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 10000"
                  value={form.previouslyPaid}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="fee-grid">
              <div className="fee-field">
                <label htmlFor="amountPaid">Amount Paid Now (PKR)</label>
                <input
                  id="amountPaid"
                  name="amountPaid"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 10000"
                  value={form.amountPaid}
                  onChange={handleChange}
                />
                {errors.amountPaid && <span className="fee-error">{errors.amountPaid}</span>}
              </div>

              <div className="fee-field">
                <label htmlFor="paymentMode">Payment Mode</label>
                <select
                  id="paymentMode"
                  name="paymentMode"
                  value={form.paymentMode}
                  onChange={handleChange}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Online / Card">Online / Card</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
            </div>

            {isInstallment && (
              <div className="fee-field">
                <label htmlFor="dueDate">Next Installment Due Date</label>
                <input
                  id="dueDate"
                  name="dueDate"
                  type="date"
                  value={form.dueDate}
                  onChange={handleChange}
                />
              </div>
            )}

            <div className="fee-field">
              <label htmlFor="remarks">Remarks (optional)</label>
              <textarea
                id="remarks"
                name="remarks"
                rows={2}
                placeholder="Any additional note"
                value={form.remarks}
                onChange={handleChange}
              />
            </div>

            <div className="fee-actions">
              <button type="button" className="fee-btn fee-btn-ghost" onClick={handleReset}>
                Clear
              </button>
              <button type="submit" className="fee-btn fee-btn-primary">
                Preview Receipt
              </button>
            </div>
          </form>
        ) : (
          <div className="fee-preview-wrap">
            <div className="fee-receipt" ref={previewRef}>
              <div className="fee-receipt-top">
                <img
                  src={logo}
                  alt="Institute of Applied Languages"
                  className="fee-receipt-logo"
                  style={{ width: 56, height: 56, objectFit: "cover", borderRadius: "50%" }}
                />
                <div className="fee-receipt-heading">
                  <span className="fee-eyebrow fee-eyebrow-dark">
                    Institute Of Applied Languages
                  </span>
                  <h2 className="fee-receipt-title">Fee Receipt</h2>
                </div>
                <div className="fee-receipt-no">
                  <span className="fee-receipt-date">{submitted.date}</span>
                </div>
              </div>

              <div className="fee-receipt-divider">
                {Array.from({ length: 44 }).map((_, i) => (
                  <span key={i} />
                ))}
              </div>

              <div className="fee-receipt-body">
                <div className="fee-receipt-row">
                  <span className="fee-receipt-label">Student Name</span>
                  <span className="fee-receipt-value">{submitted.studentName}</span>
                </div>
                {submitted.fatherName && (
                  <div className="fee-receipt-row">
                    <span className="fee-receipt-label">Father Name</span>
                    <span className="fee-receipt-value">{submitted.fatherName}</span>
                  </div>
                )}
                <div className="fee-receipt-row">
                  <span className="fee-receipt-label">Form / Roll No.</span>
                  <span className="fee-receipt-value">{submitted.formNo}</span>
                </div>
                <div className="fee-receipt-row">
                  <span className="fee-receipt-label">Course / Language</span>
                  <span className="fee-receipt-value">{submitted.course}</span>
                </div>
                <div className="fee-receipt-row">
                  <span className="fee-receipt-label">Payment Type</span>
                  <span className="fee-receipt-value">
                    {submitted.feeType}
                    {isInstallment
                      ? ` (${submitted.installmentNo} of ${submitted.totalInstallments})`
                      : ""}
                  </span>
                </div>
                <div className="fee-receipt-row">
                  <span className="fee-receipt-label">Payment Mode</span>
                  <span className="fee-receipt-value">{submitted.paymentMode}</span>
                </div>
              </div>

              <div className="fee-amount-box">
                <div className="fee-amount-row">
                  <span>Total Course Fee</span>
                  <span>Rs. {formatMoney(submitted.totalFee)}</span>
                </div>
                {submitted.previouslyPaid > 0 && (
                  <div className="fee-amount-row">
                    <span>Previously Paid</span>
                    <span>Rs. {formatMoney(submitted.previouslyPaid)}</span>
                  </div>
                )}
                <div className="fee-amount-row fee-amount-highlight">
                  <span>Amount Paid Now</span>
                  <span>Rs. {formatMoney(submitted.amountPaid)}</span>
                </div>
                <div className="fee-amount-row">
                  <span>Balance Remaining</span>
                  <span>Rs. {formatMoney(submitted.balance)}</span>
                </div>
                {isInstallment && submitted.dueDate && (
                  <div className="fee-amount-row">
                    <span>Next Due Date</span>
                    <span>
                      {new Date(submitted.dueDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}
              </div>

              <div className="fee-words">
                Amount in words: <strong>{numberToWords(submitted.amountPaid)} Rupees Only</strong>
              </div>

              {submitted.remarks && (
                <div className="fee-remarks">Remarks: {submitted.remarks}</div>
              )}

              <div className="fee-receipt-footer">
                <div className="fee-signature">
                  <span className="fee-signature-line" />
                  <span>Received By</span>
                </div>
                <div className="fee-signature">
                  <span className="fee-signature-line" />
                  <span>Authorized Signature</span>
                </div>
              </div>
            </div>

            <div className="fee-actions fee-actions-preview">
              <button className="fee-btn fee-btn-ghost" onClick={handleEdit}>
                Edit Details
              </button>
              <button className="fee-btn fee-btn-primary" onClick={handleDownloadPDF}>
                Download PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}